/**
 * Scene driver for the active-mesh freezer. Babylon normally evaluates every
 * mesh on every frame. Once a loaded scene has been quiet for one second this
 * manager freezes that evaluated list, then automatically opens it again for
 * runtime mesh churn and explicit reveal requests.
 */

import type { AbstractMesh, Observer, Scene } from '@babylonjs/core';
import { UpdateManager } from './UpdateManager.js';
import { createErrorScope, ErrorCategory } from './sceneErrorHandler.js';
import {
  ACTIVE_MESH_QUIET_MS,
  createActiveMeshFreezerState,
  transitionActiveMeshFreezer,
  type ActiveMeshFreezerCommand,
  type ActiveMeshFreezerEvent,
  type ActiveMeshFreezerModel,
  type ActiveMeshFreezerStatus,
} from './activeMeshFreezerState.js';

const errorHandler = createErrorScope('ActiveMeshFreezer', ErrorCategory.ENVIRONMENT) as {
  warn: (method: string, message: string) => unknown;
};
export const ACTIVE_MESH_FREEZE_TIMEOUT_MS = 3_000;
const freezerRegistry = new WeakMap<Scene, ActiveMeshFreezer>();

type FreezeExecutor = (onSuccess: () => void, onError: (message: string) => void) => void;

export interface ActiveMeshFreezerOptions {
  now?: () => number;
  freezeExecutor?: FreezeExecutor;
}

export interface ActiveMeshFreezerSnapshot {
  state: ActiveMeshFreezerStatus;
  activeMeshes: number;
  totalMeshes: number;
  freezeCount: number;
  freezeErrors: number;
  retryCount: number;
  suspendedReasons: string[];
  lastChurnReason: string | null;
  deadlineMs: number | null;
  quietMs: number;
  skipFrustumClipping: boolean;
}

/** Return a scene's freezer when the level lifecycle attached one. */
export function getActiveMeshFreezer(scene: Scene | null | undefined): ActiveMeshFreezer | null {
  return scene ? (freezerRegistry.get(scene) ?? null) : null;
}

/** Attach one idempotent freezer per scene. */
export function attachActiveMeshFreezer(
  scene: Scene,
  options: ActiveMeshFreezerOptions = {},
): ActiveMeshFreezer {
  const existing = freezerRegistry.get(scene);
  if (existing) return existing;
  const freezer = new ActiveMeshFreezer(scene, options);
  freezerRegistry.set(scene, freezer);
  return freezer;
}

export class ActiveMeshFreezer {
  private readonly scene: Scene;
  private readonly now: () => number;
  private readonly freezeExecutor: FreezeExecutor;
  private readonly updateManager: UpdateManager;
  private model: ActiveMeshFreezerModel;
  private readonly activeMeshes = new Set<AbstractMesh>();
  private updateId: number;
  private newMeshObserver: Observer<AbstractMesh> | null;
  private removedMeshObserver: Observer<AbstractMesh> | null;
  private disposeObserver: Observer<Scene> | null;
  private freezeGeneration = 0;
  private freezeTimeout: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  constructor(scene: Scene, options: ActiveMeshFreezerOptions = {}) {
    this.scene = scene;
    this.now = options.now ?? (() => performance.now());
    this.model = createActiveMeshFreezerState(this.now());
    this.freezeExecutor =
      options.freezeExecutor ??
      ((onSuccess, onError) => {
        // Capture every enabled/visible mesh by temporarily skipping the
        // evaluation frustum, but keep per-submesh clipping on frozen frames.
        // The initial false experiment multiplied headless draw calls 4.8x;
        // true preserves the CPU-side scene-walk win without that GPU cost.
        scene.freezeActiveMeshes(false, onSuccess, onError, false, true);
      });

    this.newMeshObserver = scene.onNewMeshAddedObservable.add((mesh) => {
      this.requestRefreeze(`mesh-added:${mesh.name}`);
    });
    this.removedMeshObserver = scene.onMeshRemovedObservable.add((mesh) => {
      this.requestRefreeze(`mesh-removed:${mesh.name}`);
    });
    this.disposeObserver = scene.onDisposeObservable.add(() => this.dispose());
    this.updateManager = UpdateManager.getInstance(scene);
    this.updateId = this.updateManager.register('activeMeshFreezer', () => this.tick(), {
      throttle: 15,
    });
  }

  /** Drive the deadline check. Public only to keep NullEngine tests deterministic. */
  tick(): void {
    if (this.disposed) return;
    this.dispatch({ type: 'TICK', now: this.now() });
  }

  /** Temporarily keep the scene unfrozen for a long-lived dynamic effect. */
  suspend(reason: string): void {
    if (this.disposed) return;
    this.dispatch({ type: 'SUSPEND', reason });
  }

  /** Release one matching suspension reference. */
  resume(reason: string): void {
    if (this.disposed) return;
    this.dispatch({ type: 'RESUME', reason, now: this.now() });
  }

  /** Unfreeze now and start a fresh quiet window after non-mesh scene churn. */
  requestRefreeze(reason: string): void {
    if (this.disposed) return;
    this.dispatch({ type: 'CHURN', reason, now: this.now() });
  }

  /**
   * Reveal contract for meshes that were disabled or hidden at freeze time.
   * Already-captured meshes are a no-op, which makes polled visibility edges
   * safe to call without repeatedly opening the scene.
   */
  ensureActive(mesh: AbstractMesh | null | undefined): void {
    if (this.disposed || !mesh || this.model.status !== 'frozen') return;
    if (this.activeMeshes.has(mesh)) return;
    this.requestRefreeze(`mesh-revealed:${mesh.name}`);
  }

  snapshot(): ActiveMeshFreezerSnapshot {
    return {
      state: this.model.status,
      activeMeshes: this.activeMeshes.size,
      totalMeshes: this.scene.meshes.length,
      freezeCount: this.model.freezeCount,
      freezeErrors: this.model.totalFreezeErrors,
      retryCount: this.model.consecutiveFreezeErrors,
      suspendedReasons: [...this.model.suspendReasons.keys()],
      lastChurnReason: this.model.lastChurnReason,
      deadlineMs: this.model.deadlineMs,
      quietMs: ACTIVE_MESH_QUIET_MS,
      skipFrustumClipping: this.scene.skipFrustumClipping,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.freezeGeneration += 1;
    this.clearFreezeTimeout();
    this.unfreezeScene(false);
    this.updateManager.unregister(this.updateId);
    if (this.newMeshObserver) {
      this.scene.onNewMeshAddedObservable.remove(this.newMeshObserver);
      this.newMeshObserver = null;
    }
    if (this.removedMeshObserver) {
      this.scene.onMeshRemovedObservable.remove(this.removedMeshObserver);
      this.removedMeshObserver = null;
    }
    if (this.disposeObserver) {
      this.scene.onDisposeObservable.remove(this.disposeObserver);
      this.disposeObserver = null;
    }
    freezerRegistry.delete(this.scene);
  }

  private dispatch(event: ActiveMeshFreezerEvent): void {
    const transition = transitionActiveMeshFreezer(this.model, event);
    this.model = transition.state;
    for (const command of transition.commands) this.runCommand(command);
  }

  private runCommand(command: ActiveMeshFreezerCommand): void {
    if (command === 'unfreeze') {
      this.unfreezeScene(true);
      return;
    }
    this.beginFreeze();
  }

  private beginFreeze(): void {
    const generation = ++this.freezeGeneration;
    this.scene.skipFrustumClipping = true;
    this.clearFreezeTimeout();
    this.freezeTimeout = setTimeout(() => {
      this.freezeTimeout = null;
      if (
        this.disposed ||
        generation !== this.freezeGeneration ||
        this.model.status !== 'freezing'
      ) {
        return;
      }
      this.freezeFailed(generation, 'Babylon active-mesh freeze timed out');
    }, ACTIVE_MESH_FREEZE_TIMEOUT_MS);
    try {
      this.freezeExecutor(
        () => this.freezeSucceeded(generation),
        (message) => this.freezeFailed(generation, message),
      );
    } catch (error) {
      this.freezeFailed(generation, error instanceof Error ? error.message : String(error));
    }
  }

  private freezeSucceeded(generation: number): void {
    if (this.disposed) return;
    if (generation !== this.freezeGeneration || this.model.status !== 'freezing') {
      // Babylon's non-frame-graph path queues executeWhenReady callbacks. A
      // churn event can invalidate one before it runs; open that stale result
      // without invalidating a newer generation registered after it.
      if (this.model.status !== 'frozen') this.unfreezeScene(false);
      return;
    }

    this.clearFreezeTimeout();

    this.activeMeshes.clear();
    const active = this.scene.getActiveMeshes();
    for (let index = 0; index < active.length; index += 1) {
      const mesh = active.data[index];
      if (mesh) this.activeMeshes.add(mesh);
    }
    this.dispatch({ type: 'FREEZE_OK' });
  }

  private freezeFailed(generation: number, message: string): void {
    if (this.disposed || generation !== this.freezeGeneration || this.model.status !== 'freezing') {
      return;
    }
    this.clearFreezeTimeout();
    errorHandler.warn('freeze', message || 'Babylon could not freeze active meshes');
    this.dispatch({ type: 'FREEZE_ERR', now: this.now() });
  }

  private unfreezeScene(invalidatePending: boolean): void {
    if (invalidatePending) {
      this.clearFreezeTimeout();
      this.freezeGeneration += 1;
    }
    this.scene.unfreezeActiveMeshes();
    this.scene.skipFrustumClipping = false;
    this.activeMeshes.clear();
  }

  private clearFreezeTimeout(): void {
    if (!this.freezeTimeout) return;
    clearTimeout(this.freezeTimeout);
    this.freezeTimeout = null;
  }
}
